/*
 * Put the store's social links at the foot of the menu drawer (CI-14).
 *
 * **It clones the footer's icons rather than declaring its own.** The URLs live in one place, the
 * `social-links` block in `sections/footer-group.json`, and a block's settings are readable only by
 * the section that owns them. Re-declaring the four URLs somewhere the header could read them would
 * mean two lists to keep in step, and the day they disagree is the day one of them is wrong.
 * Cloning the rendered node instead means the menu always shows exactly what the footer shows: the
 * owner edits the socials once, in the footer, and the menu follows.
 *
 * Nothing is moved. The footer keeps its own icons; the drawer gets a copy, with ids stripped so
 * the duplicate markup cannot collide with the original.
 *
 * If the footer is not on the page, or has no socials, the drawer simply has no social row. That is
 * why this is an enhancement in script rather than markup in the drawer: there is nothing to show
 * until there is something to copy.
 */
(function () {
  'use strict';

  if (window.mhMenuLoaded) return;
  window.mhMenuLoaded = true;

  function fillDrawer() {
    var slot = document.querySelector('#header-component .menu-drawer__utility-links');
    if (!slot || slot.querySelector('.mh-menu__social')) return;

    var source = document.querySelector('.mh-footer__social');
    if (!source) return;

    /*
     * FIX 2: clone the ICON WRAPPER, not the bare `<a>`.
     *
     * This is why the drawer used to show the words "Facebook, Instagram, YouTube, TikTok" where
     * the footer shows glyphs. The baseline block (`blocks/social-links.liquid`) ships the icon
     * switched OFF and turns it on with a structural rule:
     *
     *     .social-icons__icon                                    { display: none }
     *     .social-icons__icon-wrapper:has(.social-icons__icon path) {
     *       .social-icons__icon       { display: block }
     *       .social-icons__icon-label { display: none  }
     *     }
     *
     * Every `<a>` carries BOTH a text label and the svg, and it is the `.social-icons__icon-wrapper`
     * ancestor that decides which of the two is seen. Cloning `a[href]` left that ancestor behind,
     * so the `:has()` never matched: svg stayed `display: none` and the label stayed visible. It was
     * never a sprite-context problem, which is what the old comment on the CSS guessed; it is one
     * missing wrapper element. Cloning the wrapper makes the baseline rule match in the drawer
     * exactly as it does in the footer, so the icons are the footer's icons, not a reimplementation.
     *
     * `target="_blank" rel="noopener noreferrer"` and `aria-label` are already on each `<a>` and
     * come along in the clone, so the new-tab behaviour matches the footer for free.
     */
    var wrappers = source.querySelectorAll('.social-icons__icon-wrapper');

    // Older markup, or a footer whose icons are not wrapped: fall back to the links themselves so
    // the drawer still gets a social row rather than nothing.
    var nodes = wrappers.length ? wrappers : source.querySelectorAll('a[href]');
    if (!nodes.length) return;

    var wrap = document.createElement('div');
    wrap.className = 'mh-menu__social';

    Array.prototype.forEach.call(nodes, function (node) {
      if (!node.querySelector('a[href]') && node.tagName !== 'A') return;

      var copy = node.cloneNode(true);
      copy.removeAttribute('id');
      // A cloned subtree can carry ids and aria references that would now point at two elements.
      Array.prototype.forEach.call(copy.querySelectorAll('[id]'), function (el) {
        el.removeAttribute('id');
      });
      wrap.appendChild(copy);
    });

    if (!wrap.children.length) return;

    slot.appendChild(wrap);
  }

  /*
   * FIX 3: state the submenu's expanded state explicitly.
   *
   * The accordion itself is Horizon's, switched on by `drawer_accordion` in
   * `sections/header-group.json`: a parent with children renders as `<accordion-custom><details>`
   * with a `<summary>` carrying the plus indicator, which is already operable by Enter and Space
   * and already exposes an expanded state to a screen reader, because that is what a native
   * `summary` does.
   *
   * This adds the attribute in so many words. Browsers map `details[open]` to the accessibility
   * tree on their own, but nothing in the DOM says so, which makes the requirement impossible to
   * assert and leaves the older assistive tech that reads the attribute rather than the mapping
   * with nothing to read. It cannot desynchronise: the value is always derived from `details.open`,
   * never tracked separately, and it is rewritten on load, on every `toggle` the element fires, and
   * whenever the observer in `start()` sees the attribute change underneath us.
   *
   * It lives here rather than in `snippets/header-drawer.liquid` because that snippet is a Horizon
   * baseline file and the repo's conventions forbid editing one. See DECISIONS D85.
   */
  function syncSubmenuAria() {
    var summaries = document.querySelectorAll('#header-component .menu-drawer__menu details > summary');

    Array.prototype.forEach.call(summaries, function (summary) {
      var details = summary.parentElement;
      if (!details) return;

      // Bind the listener once per element, but always fall through to the write below: the
      // attribute can be stripped off an element this has already bound. See start().
      var bound = details.mhAriaBound;
      details.mhAriaBound = true;

      /*
       * Written only when it differs from what is already there. That is what keeps the observer
       * in `start()` from feeding itself: it watches `aria-expanded`, so an unconditional
       * `setAttribute` of the same value would be a mutation, which would call this again.
       */
      var write = function () {
        var want = details.open ? 'true' : 'false';
        if (summary.getAttribute('aria-expanded') !== want) {
          summary.setAttribute('aria-expanded', want);
        }
      };

      write();
      if (!bound) details.addEventListener('toggle', write);
    });
  }

  function apply() {
    fillDrawer();
    syncSubmenuAria();
  }

  function start() {
    apply();

    /*
     * Re-apply whenever the header's DOM changes.
     *
     * Both jobs above attach to markup this script does not own, and that markup is not final at
     * load. Horizon re-renders the drawer through the Section Rendering API after the first paint
     * (`snippets/header-drawer.liquid` switches on `section.index == blank` for exactly this
     * reason), and the footer the socials are cloned from may still be streaming in. Measured on
     * the dev server, the submenu `<details>` did not exist yet when this script first ran: the
     * socials landed, the aria sync found nothing, and the attribute was still missing by the time
     * the drawer was opened.
     *
     * An observer covers every one of those orderings without needing to know which one happened.
     * Both functions are idempotent (`fillDrawer` returns early once the row is there,
     * `syncSubmenuAria` marks each `<details>` it has bound), so re-running costs a pair of failed
     * selector matches. The callback is debounced to the next frame so a burst of mutations from a
     * single re-render does not run it once per node.
     *
     * **It watches attributes as well as nodes, from `body`.** Two things had to be learned the
     * hard way here, both measured rather than guessed:
     *
     * 1. Watching `#header-component` is not enough, because a re-render can replace that element
     *    and leave the observer watching an orphan.
     * 2. The thing that actually removed `aria-expanded` was not a re-render at all. Horizon
     *    patches sections with `assets/morph.js`, which diffs the live DOM against the new server
     *    HTML and deletes any attribute the server did not send. `aria-expanded` is ours, so the
     *    server never sends it, so morph strips it. Confirmed with a MutationObserver on the
     *    summary: same node, still in the document, one `attributes` mutation removing
     *    `aria-expanded` about a second after load. A `childList` observer cannot see that, which
     *    is why watching for node changes alone left the attribute missing.
     *
     * Hence `attributes: true` with a filter, so the sync re-asserts the attribute the moment morph
     * takes it away. `write()` compares before it sets, so re-asserting is not itself a mutation and
     * this cannot feed itself.
     */
    var host = document.body;
    if (!host || typeof MutationObserver !== 'function') return;

    var queued = false;
    new MutationObserver(function () {
      if (queued) return;
      queued = true;
      requestAnimationFrame(function () {
        queued = false;
        apply();
      });
    }).observe(host, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['open', 'aria-expanded'],
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
